/*
 * This module handles Sentry initialization and error/feedback reporting.
 */

import {
  addItemToEnvelope,
  createAttachmentEnvelopeItem,
  createEventEnvelope,
  getEnvelopeEndpointWithUrlEncodedAuth,
  serializeEnvelope,
  uuid4,
  type Attachment,
  type DsnComponents,
  type Event,
  type Integration,
  type SdkMetadata,
} from "@sentry/core";
import * as Sentry from "@sentry/vue";
import { ref } from "vue";

import type { Profile } from "@ctrl/controller-rpc";

import { logger as appLogger } from "@/logger/app";
import { IDENTIFIERS } from "@/logger/constants";
import { externalServicesEnabled } from "@/utils/external-services";
import { in_electron } from "@/utils/in";
import {
  releaseName,
  SENTRY_DSN,
  SENTRY_IPC_NAMESPACE,
} from "@/utils/sentry/common";

/*
 * Globals
 */

const Dev = ref(true);

/** How long we wait for a user-report envelope to leave the client. */
const USER_REPORT_SEND_TIMEOUT_MS = 60_000;

// Re-export for callers that already import from this module.
export { userReportEventUrl } from "@/utils/sentry/common";

/** Minimal client surface needed to POST an envelope to ingest. */
export type EnvelopeHttpClient = {
  getDsn: () => DsnComponents | undefined;
  getOptions: () => {
    tunnel?: string;
    _metadata?: SdkMetadata;
  };
};

/**
 * POST an event envelope (with attachments) straight to Sentry's ingest HTTP
 * endpoint, bypassing the SDK transport.
 *
 * Needed on Electron: the renderer transport only IPCs to the main process and
 * always reports 200, while main may be disabled (dev) or offline-queued — so
 * `afterSendEvent` / `flush` are not proof the event exists in Sentry.
 */
export async function sendEventEnvelopeHttp(
  client: EnvelopeHttpClient,
  event: Event,
  attachments: Attachment[],
  timeoutMs: number
): Promise<{ ok: boolean; status: number }> {
  const dsn = client.getDsn();
  if (!dsn) return { ok: false, status: 0 };

  const options = client.getOptions();
  let envelope = createEventEnvelope(
    event,
    dsn,
    options._metadata,
    options.tunnel
  );
  for (const attachment of attachments) {
    envelope = addItemToEnvelope(
      envelope,
      createAttachmentEnvelopeItem(attachment)
    );
  }

  const url = getEnvelopeEndpointWithUrlEncodedAuth(
    dsn,
    options.tunnel,
    options._metadata?.sdk
  );
  const serialized = serializeEnvelope(envelope);
  // Copy binary envelopes into a fresh ArrayBuffer-backed view so the value
  // is a valid BodyInit under TypeScript's DOM typings (SharedArrayBuffer).
  const body: BodyInit =
    typeof serialized === "string"
      ? serialized
      : (() => {
          const copy = new Uint8Array(serialized.byteLength);
          copy.set(serialized);
          return copy;
        })();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: "POST",
      body,
      referrerPolicy: "origin",
      credentials: "omit",
      signal: controller.signal,
    });
    return { ok: response.ok, status: response.status };
  } catch (e) {
    window.console.warn("[Sentry] Direct envelope upload failed:", e);
    return { ok: false, status: 0 };
  } finally {
    clearTimeout(timer);
  }
}

async function collectLogAttachments(): Promise<Attachment[]> {
  // Access controller from globalThis where it's stored in App.vue
  const controller = globalThis.controller;
  if (!controller) return [];

  try {
    // 2000 entries ≈ 15–80 minutes of steady-state history (~3 MB of text at
    // the observed ~1.6 KB/entry, well under Sentry's 20 MB attachment
    // limit); boot/sync bursts can still compress the window to seconds.
    const TRIM_ENTRIES = 2000;
    const allLogs = await appLogger.app.collect(controller, TRIM_ENTRIES);
    const attachments: Attachment[] = [];

    if (allLogs[IDENTIFIERS.APP]) {
      attachments.push({
        filename: "app.logs.txt",
        data: allLogs[IDENTIFIERS.APP],
        contentType: "text/plain",
      });
    }
    if (allLogs[IDENTIFIERS.DASHBOARD]) {
      attachments.push({
        filename: "dashboard.logs.txt",
        data: allLogs[IDENTIFIERS.DASHBOARD],
        contentType: "text/plain",
      });
    }
    if (allLogs[IDENTIFIERS.MENUBAR]) {
      attachments.push({
        filename: "menubar.logs.txt",
        data: allLogs[IDENTIFIERS.MENUBAR],
        contentType: "text/plain",
      });
    }
    if (allLogs[IDENTIFIERS.CONTROLLER]) {
      attachments.push({
        filename: "controller.logs.txt",
        data: allLogs[IDENTIFIERS.CONTROLLER],
        contentType: "text/plain",
      });
    }

    return attachments;
  } catch (e) {
    window.console.warn("[Sentry] collectLogAttachments failed:", e);
    return [];
  }
}

/*
 * Public functions
 */

export const sentry = {
  /**
   * Initialize Sentry with the provided configuration.
   */
  async initialize(
    dist: string,
    version: string,
    uid: string,
    profile: Profile,
    dev: boolean
  ): Promise<void> {
    if (!externalServicesEnabled) return;
    if (Sentry.getClient() !== undefined) return;

    // Save the mode.
    Dev.value = dev;

    // Extract user info from profile
    const email = profile.email;
    const name =
      profile.kind.provider === "google" ? profile.kind.userinfo.name : email;

    const options = {
      dist: dist,
      dsn: SENTRY_DSN,
      release: releaseName(version),
      // Transient browser-level selection desync in the editor (Android
      // IME/autocorrect vs prosemirror-view, FRONTEND-FHH): `Selection.
      // collapse` throws when the IME mutated a text node before the view
      // re-synced. It heals on the next editor update and prosemirror-view
      // leaves the call unguarded upstream, so drop the reports. Matched by
      // exception type: the message wording differs per browser.
      ignoreErrors: ["IndexSizeError"],
      // [tag:sentry-disable-error-handlers] Disable default handlers, we send everything ourselves
      integrations: function (integrations: Integration[]) {
        integrations = integrations.filter(function (integration: Integration) {
          return (
            integration.name !== "GlobalHandlers" &&
            // "BrowserApiErrors" is the Sentry v8+ name of "TryCatch"; the
            // stale name let it slip back in, reporting listener exceptions
            // outside our pipeline — without log attachments (FRONTEND-FHH).
            integration.name !== "BrowserApiErrors" &&
            integration.name !== "TryCatch" &&
            // [tag:vue-sentry-late-binding] Configure the Vue-Sentry integration with a late binding
            // See https://docs.sentry.io/platforms/javascript/guides/vue/#configuration-for-late-defined-vue-apps
            integration.name !== "Vue"
          );
        });
        integrations.push(Sentry.browserTracingIntegration());
        return integrations;
      },
      tracesSampleRate: 1.0,
    };

    if (in_electron) {
      const ElectronSentry = await import("@sentry/electron/renderer");
      ElectronSentry.init(
        {
          ...options,
          ipcNamespace: SENTRY_IPC_NAMESPACE,
        },
        Sentry.init
      );
    } else {
      Sentry.init(options);
    }

    // Set user information
    Sentry.setUser({
      email: email,
      id: uid,
      username: name,
    });
  },

  /**
   * Check if Sentry client is initialized and available.
   */
  isInitialized(): boolean {
    return Sentry.getClient() !== undefined;
  },

  /**
   * Get the Sentry client instance.
   */
  getClient() {
    return Sentry.getClient();
  },

  /**
   * Mint a Sentry event ID locally (Featurebase can store it before upload).
   */
  newEventId(): string {
    return uuid4();
  },

  /**
   * Capture a user-report event with log attachments for a pre-minted event ID,
   * and wait until Sentry's ingest HTTP endpoint accepts the envelope.
   *
   * Uses a direct fetch (not the SDK transport) so Electron's renderer→main
   * IPC fake-200 cannot report success for events that never reach Sentry.
   *
   * Returns true only on HTTP success. The event ID is always the one passed in
   * (generated locally by the caller).
   */
  async captureEvent(name: string, eventId: string): Promise<boolean> {
    const client = this.getClient();
    if (!client) {
      return false;
    }

    try {
      const randomId = Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0");
      const message = `User report #${randomId} (${name})`;
      const attachments = await collectLogAttachments();

      const event = await client.eventFromMessage(message, "info");
      event.event_id = eventId;
      event.level = "info";
      event.tags = {
        ...(event.tags || {}),
        reportType: "user",
        reportId: randomId,
      };

      // Copy identity / release from the live SDK so the event matches other
      // reports, without going through the (Electron IPC) transport.
      const options = client.getOptions();
      if (options.release) event.release = options.release;
      if (options.environment) event.environment = options.environment;
      if (options.dist) event.dist = options.dist;
      const user = Sentry.getCurrentScope().getUser();
      if (user) event.user = user;

      const { ok, status } = await sendEventEnvelopeHttp(
        client,
        event,
        attachments,
        USER_REPORT_SEND_TIMEOUT_MS
      );

      if (!ok) {
        window.console.warn(
          "[Sentry] User report was not accepted by ingest:",
          { eventId, status }
        );
        return false;
      }

      return true;
    } catch (e) {
      window.console.warn("Failed to capture Sentry feedback event:", e);
      return false;
    }
  },

  /**
   * Capture Sentry "User Feedback" tied to an existing eventId.
   * Sent via direct HTTP (same reason as {@link captureEvent}).
   */
  async captureUserFeedback(
    associatedEventId: string,
    name: string,
    email: string,
    message: string
  ): Promise<boolean> {
    const client = this.getClient();
    if (!client) return false;

    try {
      const eventId = uuid4();
      const event: Event = {
        event_id: eventId,
        type: "feedback",
        level: "info",
        contexts: {
          feedback: {
            contact_email: email,
            name,
            message,
            associated_event_id: associatedEventId,
          },
        },
      };

      const options = client.getOptions();
      if (options.release) event.release = options.release;
      if (options.environment) event.environment = options.environment;
      if (options.dist) event.dist = options.dist;
      const user = Sentry.getCurrentScope().getUser();
      if (user) event.user = user;

      const { ok, status } = await sendEventEnvelopeHttp(
        client,
        event,
        [],
        USER_REPORT_SEND_TIMEOUT_MS
      );

      if (!ok) {
        window.console.warn(
          "[Sentry] User feedback was not accepted by ingest:",
          { associatedEventId, status }
        );
      }
      return ok;
    } catch (e) {
      window.console.warn("Failed to capture Sentry user feedback:", e);
      return false;
    }
  },

  /**
   * Capture a handled error with the specified mechanism: the app caught it,
   * surfaced it to the user and recovered, but it is a bug worth a report
   * (e.g. a recording processing failure). Unlike {@link captureFatalError},
   * the event is level "error" and marked handled, so it does not register
   * as a crash. The app logs are attached for diagnosis.
   */
  async captureHandledError(error: Error, mechanism: string): Promise<void> {
    // Ignore in dev mode, like fatal errors.
    if (Dev.value) return;
    const client = this.getClient();
    if (!client) return;
    const event = await client.eventFromException(error);
    const attachments = await collectLogAttachments();
    event.tags = { ...(event.tags || {}), reportType: "handled" };
    event.level = "error";
    for (const exception of event.exception?.values || []) {
      if (exception.mechanism) {
        exception.mechanism.handled = true;
        exception.mechanism.type = mechanism;
      }
    }
    Sentry.withScope((scope) => {
      for (const attachment of attachments) {
        scope.addAttachment(attachment);
      }
      Sentry.captureEvent(event);
    });
  },

  /**
   * Capture a fatal error with the specified mechanism.
   */
  async captureFatalError(error: Error, mechanism: string): Promise<void> {
    // Ignore fatal in dev mode.
    if (Dev.value) return;
    const client = this.getClient();
    if (client) {
      const event = await client.eventFromException(error);
      const attachments = await collectLogAttachments();
      event.tags = { ...(event.tags || {}), reportType: "fatal" };
      event.level = "fatal";
      for (const exception of event.exception?.values || []) {
        if (exception.mechanism) {
          exception.mechanism.handled = false;
          exception.mechanism.type = mechanism;
        }
      }
      Sentry.withScope((scope) => {
        for (const attachment of attachments) {
          scope.addAttachment(attachment);
        }
        Sentry.captureEvent(event);
      });
    } else {
      // Fallback if client not available
      Sentry.captureException(error);
    }
  },

  /**
   * Capture a fatal message.
   */
  captureFatalMessage(message: string): void {
    // Ignore fatal in dev mode.
    if (Dev.value) return;
    // Note: this path can't attach logs (async) without changing signature.
    Sentry.withScope((scope) => {
      scope.setTag("reportType", "fatal");
      Sentry.captureMessage(message, { level: "fatal" });
    });
  },
};
